import { getDataFromPackManager, getRouterData, RouterData } from 'packmanager-communication-lib';
import React, { createContext, Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { Bot, Category, Page } from '../interfaces';
import { ApiService } from '../services/Api';
import { createApiService } from '../services/ApiFactory';
import { fetchReplies } from '../services/CategoryService';
import { fetchBotData } from '../services/PacksService';
export interface AppContextData {
  routerData: RouterData;
  apiService: ApiService;
  packsService: ApiService;
  botList: Array<Bot>;
  page: Page;
  setPage: Dispatch<SetStateAction<Page>>;
  categories: Category[];
  fetchReplies: (apiService: ApiService, botRef: Bot) => Promise<Category[] | undefined>;
  initialized: boolean;
  selectedCategory: Category;
  setCategories: Dispatch<SetStateAction<Category[]>>;
  setSelectedCategory: Dispatch<SetStateAction<Category>>;
  setInitialized: Dispatch<SetStateAction<boolean>>;
  botRef: Bot | null;
}

const initValue: AppContextData = {
  routerData: {},
  apiService: {} as ApiService,
  packsService: {} as ApiService,
  botList: [],
  page: 'CategorySelection',
  setPage: () => {},
  categories: [],
  fetchReplies: () => Promise.resolve(undefined),
  initialized: false,
  selectedCategory: {} as Category,
  setCategories: () => {},
  setSelectedCategory: () => {},
  setInitialized: () => {},
  botRef: {} as Bot,
};

export const AppContext = createContext<AppContextData>(initValue);

export const AppProvider = ({ children }: { children: React.ReactNode }) => {
  const [initialized, setInitialized] = useState<boolean>(false);
  const [routerData, setRouterData] = useState<RouterData>({} as RouterData);
  const [apiService, setApiService] = useState<ApiService>({} as ApiService);
  const [packsService, setPacksService] = useState<ApiService>({} as ApiService);
  const [botRef, setBotRef] = useState(null);
  const [botList, setBotList] = useState<Bot[]>([]);
  const [page, setPage] = useState<Page>('CategorySelection');
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<Category>({} as Category);

  const fetchRouterData = async () => {
    const routerData: RouterData = await getRouterData();
    setRouterData(routerData);
  };

  const setApis = async () => {
    const accessToken: string = (await getDataFromPackManager('accountToken')) as unknown as string;
    const goPersonal = createApiService('goPersonal', accessToken);
    const packs = createApiService('packs', accessToken);

    setApiService(goPersonal);
    setPacksService(packs);
  };

  useEffect(() => {
    setInitialized(false);
    fetchRouterData();
    setApis();
  }, []);

  useEffect(() => {
    const getBotData = async () => {
      const botData = await fetchBotData(packsService);
      if (botData) {
        // @ts-expect-error - Type inference issue with external API response
        setBotRef(botData.botRef);
        setBotList(botData.botList as Bot[]);
      }
    };

    if (Object.keys(packsService).length !== 0) {
      getBotData();
    }
  }, [packsService]);

  useEffect(() => {
    const fetchRepliesData = async () => {
      const categories = await fetchReplies(apiService, botRef);
      if (categories) setCategories(categories);
      setInitialized(true);
    };

    if (Object.keys(apiService).length > 0 && botRef) {
      fetchRepliesData();
    }
  }, [apiService, botRef]);

  const contextValue = useMemo(
    () => ({
      routerData,
      apiService,
      botList,
      botRef,
      packsService,
      page,
      setPage,
      categories,
      fetchReplies,
      initialized,
      selectedCategory,
      setCategories,
      setSelectedCategory,
      setInitialized,
    }),
    [routerData, apiService, botList, botRef, packsService, page, categories, initialized, selectedCategory],
  );

  return initialized ? <AppContext.Provider value={contextValue}>{children}</AppContext.Provider> : <LoadingSpinner />;
};

export function useAppContext(): AppContextData {
  const context = React.useContext(AppContext);
  if (!context) {
    throw new Error('use app context must be used within an AppProvider');
  }

  return context;
}
